'use strict';
import { guuid } from '@aretheyhappy/utils';
import Flash from '../Flash';
import { FrontendLogger } from '../util/FrontendLogger';
export var PopoverServiceEvents;
(function (PopoverServiceEvents) {
    // Request popover to be opened
    PopoverServiceEvents["POPOVER_OPEN"] = "global-popover:open:controller";
    // Popover was closed from the service
    PopoverServiceEvents["POPOVER_CLOSED_SERVICE"] = "global-popover:close:controller-service";
    // Popover was closed from inside itself
    PopoverServiceEvents["POPOVER_CLOSED_INTERNAL"] = "global-popover:close:controller-internal";
})(PopoverServiceEvents || (PopoverServiceEvents = {}));
/**
 * Utility function to create a i18n-based tooltip.
 * @param title Title of the tooltip
 * @param lbl Label of the tooltip
 * @param help (default=false) Help link
 * @param i18n (default={}) Additional I18N properties to provide.
 * @returns
 */
function i18nTooltip(title, lbl, help = false, i18n = {}) {
    return new PopoverConfig()
        .setFocus(true)
        .setClass('el--t-tooltip')
        .setI18NObject(Object.assign({}, { title, lbl }, i18n))
        .setHelpArticle(help);
}
/**
 * Create a component tooltip
 * @param comp
 * @deprecated This is redundant to use, just use `setComponent` when building a popover.
 * @returns
 */
function compTooltip(comp) {
    return new PopoverConfig()
        .setComponent(comp);
}
/**
 * Config builder used for popovers
 */
class PopoverConfig {
    constructor() {
        this.uid = guuid();
        // Component to render (VueComponent)
        this.comp = false;
        // Optional target to render the popover against
        this.target = false;
        // I18N object to render
        this.i18n = false;
        // Class to add
        this.class = false;
        // Should the popover request focus
        this.focus = false;
        // Callback on close
        this.next = false;
        // Whether or not this popover is moveable
        this.moveable = false;
        // Whether or not to take this popover fullscreen on mobile
        this.mobile_full = false;
        // Helpcenter article (String)
        this.help = false;
        // Don't add padding
        this.nopad = false;
        // Set to true to allow a modal to be closed with the escape key
        this.esckill = true;
        // Event that triggered the popover
        this.event = undefined;
        /**
         * Which global popover index to use. This is determined by the index defined when you
         * add a global popover to your page. This allows you to have popover that trigger popovers.
         *
         * Not setting it uses 0
         * <ath-global-popover />
         *
         * Providing another value allows you to have a secondary popover.
         * <ath-global-popover :popoverIndex="'1'" />
         *
         * @deprecated GlobalPopoverController now handles this
         */
        this.index = '0';
        // Props to bind to the popover component
        this.props = {};
        // Version 2 placement. Defaults to true when using PopoverConfig.
        // Older popovers that have not been converted explicity set this to false.
        this.v2 = true;
        // What side to put the popover on
        this.placement = 'bottom';
        // If the arrow should be shown
        this.show_arrow = false;
        // Hide the popover on mouse leave. This will dismiss the popover when the mouse leaves it.
        this.hide_on_mouse_leave = false;
        // Stop event propagation on close
        this.stop_propagation_on_close = true;
        /**
         * Should stick to a specific quadrant (number [1, 2, 3, 4])
         * @deprecated This is used for backwards compatability to the old positioning format.
         */
        this.quadrant = false;
        /**
         * Should stick to a specific section ['bottom', 'left', 'top', 'right']
         * @deprecated This is used for backwards compatability to the old positioning format.
         */
        this.section = false;
        /**
         * Use absolute or relative positioning
         * @deprecated This is used for backwards compatability to the old positioning format.
         */
        this.absolute = true;
    }
    setComponent(value) { this.comp = value; return this; }
    setTarget(value) { this.target = value; return this; }
    setI18NObject(value) { this.i18n = value; return this; }
    setClass(value) { this.class = value; return this; }
    setFocus(value) { this.focus = value; return this; }
    setNext(value) { this.next = value; return this; }
    setMovable(value) { this.moveable = value; return this; }
    setMobileFull(value) { this.mobile_full = value; return this; }
    setHelpArticle(value) { this.help = value; return this; }
    setNoPad(value) { this.nopad = value; return this; }
    setEscKill(value) { this.esckill = value; return this; }
    setEvent(value) { this.event = value; return this; }
    /**
     * @deprecated Index is now handled internally and not needed on the config
     * @param value
     * @returns
     */
    setIndex(value) { this.index = value; return this; }
    setProps(value) { this.props = value; return this; }
    setV2(value) { this.v2 = value; return this; }
    setPlacement(value) { this.placement = value; return this; }
    setShowArrow(value) { this.show_arrow = value; return this; }
    setHideOnMouseLeave(value) { this.hide_on_mouse_leave = value; return this; }
    setStopPropagationOnClose(value) { this.stop_propagation_on_close = value; return this; }
    /**
     * Data payload to be passed to the popover. These will be passed to
     * a prop called `popoverdata` in your component. You must define this prop.
     * Because of this, it is advised to no longer use this in favor of
     * `setProps` as these allow you do decide prop names.
     *
     * props: {
     *     popoverdata: {type: Object, default: () => ({})},
     * },
     *
     * @deprecated Use `setProps` instead.
     */
    setPopoverData(value) {
        this.props.popoverdata = value;
        return this;
    }
    /**
     * @deprecated - Use V2 Placement
     */
    setQuadrant(value) { this.quadrant = value; return this; }
    /**
     * @deprecated - Use V2 Placement
     */
    setSection(value) { this.section = value; return this; }
    /**
     * @deprecated - Use V2 Placement
     */
    setAbsolute(value) { this.absolute = value; return this; }
}
/**
 * Popover service used to create new system popovers
 */
class PopoverService {
    /**
     * Open a new popover on the screen
     * @param {*} config - PopoverConfig class (legacy config will be converted)
     * @param {*} event - (deprecated) - Event that triggered the popover. Moved to PopoverConfig.
     * @param {*} data - (deprecated) - data event to pass to component. Moved to PopoverConfig.
     * @param {*} next - (deprecated) - next function to call. Moved to PopoverConfig.
     * @param {*} index - Index of global popover to use.
     * @deprecated Start using PopoverConfig and switch to openPopover
     */
    static open(config, event = undefined, data = {}, next = false, index = '0') {
        FrontendLogger.info('PopoverService.open', config);
        // Legacy compatibility. If we were not sent a popover config, convert it to one.
        if (config.constructor?.name !== PopoverConfig.name) {
            const legacyConfig = Object.assign({}, {
                comp: false,
                target: false,
                i18n: false,
                class: false,
                focus: false,
                quadrant: false,
                section: false,
                next: false,
                moveable: false,
                mobile_full: false,
                help: false,
                nopad: false,
                data: false,
                absolute: true,
                esckill: true, //  Set to true to allow a modal to be closed with the escape key
            }, config, { data, next, event });
            const convertedConfig = new PopoverConfig()
                .setComponent(legacyConfig.comp)
                .setTarget(legacyConfig.target)
                .setI18NObject(legacyConfig.i18n)
                .setClass(legacyConfig.class)
                .setFocus(legacyConfig.focus)
                .setQuadrant(legacyConfig.quadrant)
                .setSection(legacyConfig.section)
                .setNext(legacyConfig.next)
                .setMovable(legacyConfig.moveable)
                .setMobileFull(legacyConfig.mobile_full)
                .setHelpArticle(legacyConfig.help)
                .setNoPad(legacyConfig.nopad)
                .setPopoverData(legacyConfig.data)
                .setAbsolute(legacyConfig.absolute)
                .setEscKill(legacyConfig.esckill)
                .setEvent(legacyConfig.event)
                .setIndex(index)
                // Make legacy compatibility not use V2 placement by default.
                .setV2(false);
            config = convertedConfig;
        }
        Flash.publish(`global-popover:open:${config.index}`, config);
        Flash.publish(PopoverServiceEvents.POPOVER_OPEN, config);
    }
    /**
     * Open a new popover with the specified config.
     * @param config
     */
    static openPopover(config) {
        FrontendLogger.info('PopoverService.openPopover', config);
        Flash.publish(`global-popover:open:${config.index}`, config);
        Flash.publish(PopoverServiceEvents.POPOVER_OPEN, config);
    }
    /**
     * Close the popover by index
     * @param {*} index
     */
    static close(index = '0') {
        FrontendLogger.info('PopoverService.close');
        Flash.publish(`global-popover:close:${index}`);
        Flash.publish(PopoverServiceEvents.POPOVER_CLOSED_SERVICE);
    }
}
export { i18nTooltip, compTooltip, PopoverConfig, PopoverService, };
export default PopoverService;
//# sourceMappingURL=PopoverService.js.map